'use client';

import React, { useEffect, useMemo } from 'react';

import { usePathname } from 'next/navigation';

import { usePages } from '@/api/usePages';
import { usePortalBoot } from '@/api/usePortalBoot';
import { CmsImage } from '@/components/CmsImage';
import { cn } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import { AcCircularLoader } from '@/components/portal-ui';
import { getCustomLoaderAnimation } from '@/lib/loaderAnimation';

import MenuItem from './MenuItem';
import { StyledMenuScope, useMenuScopeProps } from './menuStyle';

type MenuProps = React.HTMLAttributes<HTMLDivElement>;

export const Menu: React.FC<MenuProps> = ({ className }) => {
  const { initialBoot, playerData, setCurrentPageName, currentLanguage, domain } = useAppState();
  const pathname = usePathname();
  const { getPages } = usePages({ portalId: initialBoot?.portalId });
  const { getPortalBoot } = usePortalBoot();

  const menuScopeProps = useMenuScopeProps();

  const getMenuItemIcon = (pageTemplate: string, icon: string) => {
    return (
      <CmsImage
        src={icon}
        alt={pageTemplate}
        width={46}
        height={46}
        className="menu-icon my-2.5 size-[46px] shrink-0"
      />
    );
  };

  const pages = useMemo(() => {
    if (playerData?.playerId && initialBoot?.portalId) {
      return getPages.data;
    }

    return getPortalBoot.data?.pages || initialBoot?.pages;
  }, [
    getPages.data,
    getPortalBoot.data?.pages,
    initialBoot?.pages,
    playerData?.playerId,
    initialBoot?.portalId,
    currentLanguage,
  ]);

  const isLoading = useMemo(() => {
    return playerData?.playerId && getPages.isLoading;
  }, [playerData?.playerId, getPages.isLoading]);

  const sortedPages = useMemo(() => {
    if (!pages) return [];

    return Object.entries(pages).sort(([, a], [, b]) => a.priority - b.priority);
  }, [pages]);

  const isPagesEmpty = !pages || Object.keys(pages).length === 0;

  useEffect(() => {
    if (isPagesEmpty) return;

    const currentPage = Object.entries(pages).find(([, page]) => {
      const slug = page.slug ?? '';
      const pagePath = slug === 'home' ? '/' : `/${slug}`;

      return pagePath === pathname;
    });

    if (currentPage) {
      setCurrentPageName(currentPage[1].pageName);
    } else if (sortedPages.length > 0) {
      setCurrentPageName(sortedPages[0][1].pageName);
    }
  }, [isPagesEmpty, pages, pathname, setCurrentPageName, sortedPages]);

  const groupedPages = useMemo(() => {
    if (!Array.isArray(pages) || pages.length === 0) return [];

    const mainPages: typeof pages = [];
    const subPagesByParent: Record<string, typeof pages> = {};

    for (const page of pages) {
      if (page.isSubPage) {
        const parentId = page.parentPageId!;
        if (!subPagesByParent[parentId]) {
          subPagesByParent[parentId] = [];
        }
        subPagesByParent[parentId].push(page);
      } else {
        mainPages.push(page);
      }
    }

    return mainPages
      .map((page) => ({
        ...page,
        subPages: (subPagesByParent[page.id] || []).sort(
          (a, b) => (a.subPagePriority ?? 0) - (b.subPagePriority ?? 0)
        ),
      }))
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [pages]);

  return (
    <StyledMenuScope
      {...menuScopeProps}
      className={cn(
        'menu-container hidden h-full w-full overflow-y-auto overflow-x-hidden rounded-[20px] p-3 pb-12 md:flex',
        className
      )}
      data-testid="PortalSidebar-Container"
    >
      <div className="w-full space-y-4 py-4 pt-2" data-testid="PortalSidebar-Content">
        <div
          className="menu-items flex w-full flex-col gap-[16px]"
          data-testid="PortalSidebar-Items"
        >
          {isLoading ? (
            <div
              className="flex size-full items-center justify-center"
              data-testid="PortalSidebar-Loading"
            >
              <AcCircularLoader animationUrl={getCustomLoaderAnimation(initialBoot?.theme?.general?.loaderAnimation)} />
            </div>
          ) : (
            groupedPages.map((page, index) => {
              return (
                <div
                  key={page.id || index}
                  data-priority={page.priority}
                  data-testid={`PortalSidebar-Item-${page.slug}`}
                  className="w-full"
                >
                  <MenuItem
                    data-testid={`PortalSidebar-Item-Link-${page.slug}`}
                    icon={getMenuItemIcon(page.pageTemplate, page.pageIcon)}
                    title={page.pageName}
                    to={page.slug === 'home' ? '/' : `/${page.slug}`}
                    subPages={page.subPages}
                    redirectConfig={page.redirectConfig}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </StyledMenuScope>
  );
};
